import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import FormControlLabel from "@mui/material/FormControlLabel";
import { AppContext } from "../../../contexts/AppContext";
import { useContext } from "react";
import Loader from "../LoadScreen/Loader";
import { countriesList } from "../../../utils/location";
import { useTranslation } from "react-i18next";

/**
 * A dialog component that allows the user to take a screenshot of the map.
 *
 * @param {{ open: boolean, onClose: function, onClick: function, handleScreenshotRegion: function, screenshotRegion: object, loadingScreenshot: boolean, formatOptions: object, setFormatOptions: function }} props - The props object.
 * @returns {JSX.Element} The JSX element representing the ScreenshotDialog component.
 */
export default function ScreenshotDialog({
  open,
  onClose,
  onClick,
  handleScreenshotRegion,
  screenshotRegion,
  loadingScreenshot,
  formatOptions,
  setFormatOptions,
}) {
  const { t } = useTranslation();
  const { pathLocation } = useContext(AppContext);

  /**
   * Handles the change event of the select input.
   * @param {Object} event - The event object.
   */
  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    const country = countriesList.find((country) => country.NAME_0 === value);
    handleScreenshotRegion({
      name: value,
      isoCode: country.isoCode,
      gid0: country.GID_0,
    });
  };

  /**
   * Resets the screenshot region and format options.
   */
  const handleReset = () => {
    handleScreenshotRegion({ name: "", isoCode: "", gid0: "" });
    setFormatOptions((prevState) => ({
      ...prevState,
      pdf: true,
      image: false,
    }));
  };

  return (
    <Box>
      {loadingScreenshot && (
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "40%",
            p: 2,
          }}
        >
          <Loader size={50} thickness={5}>
            Preparing Screenshot
          </Loader>
        </Paper>
      )}
      <Dialog open={open} onClose={onClose}>
        <DialogTitle sx={{ textTransform: "capitalize" }}>
          {t("screenshot.title")}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" my={1}>
            <TextField
              fullWidth
              size="small"
              select
              label={t("screenshot.body.countries")}
              value={screenshotRegion.name}
              SelectProps={{
                MenuProps: { style: { maxHeight: 200 } },
              }}
              onChange={handleChange}
              helperText={t("screenshot.body.helperText")}
            >
              {countriesList.map((option) => (
                <MenuItem dense key={option.GID_0} value={option.NAME_0}>
                  {option.NAME_0}
                </MenuItem>
              ))}
            </TextField>
            <FormControl sx={{ my: 2 }} component="fieldset" variant="standard">
              <FormLabel component="legend">
                {t("screenshot.body.formats")}
              </FormLabel>
              <FormGroup sx={{ display: "flex" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(e) =>
                        setFormatOptions({
                          ...formatOptions,
                          pdf: e.target.checked,
                        })
                      }
                      checked={formatOptions.pdf}
                    />
                  }
                  label="PDF"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(e) =>
                        setFormatOptions({
                          ...formatOptions,
                          image: e.target.checked,
                        })
                      }
                      checked={formatOptions.image}
                    />
                  }
                  label={t("screenshot.body.label")}
                />
              </FormGroup>
              <FormHelperText>
                {t("screenshot.body.formHelperText")}
              </FormHelperText>
            </FormControl>

            <Button
              variant="contained"
              size="small"
              color="error"
              sx={{ alignSelf: "flex-end" }}
              onClick={handleReset}
            >
              {t("screenshot.body.reset")}
            </Button>
          </Box>
          <Stack>
            <Alert sx={{ my: 1 }} variant="filled" severity="info">
              {t("screenshot.body.warning1")}
            </Alert>
            <Alert sx={{ my: 1 }} variant="filled" severity="info">
              {" "}
              {t("screenshot.body.warning2", {
                region: pathLocation.substring(1).toUpperCase(),
              })} 
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={Object.values(formatOptions).every(
              (value) => value === false
            )}
            variant="contained"
            onClick={onClick}
            color="primary"
          >
            {t("screenshot.body.downloadScreenshot")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
